import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  IncomeDetailsGroupDTO,
  IncomeDetailsRowDTO,
  useIncomeDetailsQuery,
} from '../../api/useIncomeDetailsQuery';
import { AsyncComponentHookResult } from '../../../Types/ComponentHookResult';
import {
  COLUMNS,
  formatCellValue,
  IncomeDetailsData,
  Row,
  SortBy,
  TableData,
} from './IncomeDetails.meta';
import { SERIES_COLORS } from '../../../CommonComponents/OPL/charts/charts.constants';
import { PerformancePeriodDTO, TabSlug } from '../../types';
import { selectGroupSelectionData } from '../../state/selectors';
import {
  setGroupSelectionDefaultTabIndex,
  setGroupSelectionLoadingStatus,
  setGroupSelectionTabsList,
} from '../../state/actions';

const sortingFn = (
  a: IncomeDetailsGroupDTO | IncomeDetailsRowDTO,
  b: IncomeDetailsGroupDTO | IncomeDetailsRowDTO,
  sortBy: SortBy,
) => {
  const aValue = a[sortBy.column];
  const bValue = b[sortBy.column];

  if (aValue == null && bValue == null) {
    return 0;
  }

  if (sortBy.column === 'name' || sortBy.column === 'ticker') {
    return sortBy.sort === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
  }

  if (aValue == null) {
    return sortBy.sort === 'asc' ? 1 : -1;
  }

  if (bValue == null) {
    return sortBy.sort === 'asc' ? -1 : 1;
  }

  return sortBy.sort === 'asc' ? aValue - bValue : bValue - aValue;
};

const sortGroups = (groups: (IncomeDetailsGroupDTO & { id: string })[], sortBy: SortBy) => {
  return [...groups].sort((a, b) => sortingFn(a, b, sortBy));
};

const sortRows = (rows: IncomeDetailsRowDTO[], sortBy: SortBy) => {
  return [...rows].sort((a, b) => sortingFn(a, b, sortBy));
};

const filterRows = (rows: Row[], filter: string) => {
  if (!filter) {
    return rows;
  }

  const rowsWithFilteredChildRows = rows.map(row => ({
    ...row,
    childRows: (row.childRows || []).filter(({ cells }) =>
      cells.some(({ text }) => (text || '').toLowerCase().includes(filter.toLowerCase())),
    ),
  }));

  return rowsWithFilteredChildRows.filter(row => {
    if (row.childRows.length > 0) {
      return true;
    }

    return row.cells.some(({ text }) => (text || '').toLowerCase().includes(filter.toLowerCase()));
  });
};

interface Params {
  period?: PerformancePeriodDTO;
  startDate?: string;
  endDate?: string;
  accountId: string;
  groupId: string;
  sortBy: SortBy;
  filter: string;
}

export const useIncomeDetails = ({
  period,
  startDate,
  endDate,
  accountId,
  groupId,
  sortBy,
  filter,
}: Params): AsyncComponentHookResult<IncomeDetailsData> => {
  const dispatch = useDispatch();

  const queryResult = useIncomeDetailsQuery({
    period,
    startDate,
    endDate,
    accountId,
    groupId,
  });

  useEffect(() => {
    dispatch(setGroupSelectionLoadingStatus(queryResult.isLoading, TabSlug.Income));
  }, [dispatch, queryResult.isLoading]);

  const {
    tabList: stateTabList,
    defaultTabIndex: stateDefaultTabIndex,
    selectedTabIndex: stateSelectedTabIndex,
  } = useSelector(selectGroupSelectionData(TabSlug.Income));

  const data = useMemo(() => {
    if (!queryResult.data) {
      return null;
    }

    const { categories, defaultGroup } = queryResult.data;

    const tabList = stateTabList ?? categories.map(({ name }) => name);

    const defaultTabIndex =
      stateDefaultTabIndex ?? tabList.findIndex(group => group === defaultGroup);

    const tabIndex = stateSelectedTabIndex ?? (defaultTabIndex === -1 ? 0 : defaultTabIndex);

    const category = categories[tabIndex];

    if (!category) {
      return null;
    }

    const groups = category.groups.map((group, index) => ({
      ...group,
      id: `${group.name}--${index}`,
      color: group.color || SERIES_COLORS[index % SERIES_COLORS.length],
    }));

    const sortedGroups =
      sortBy.sort !== 'none'
        ? sortGroups(groups, sortBy).map(group => ({
            ...group,
            rows: sortRows(group.rows, sortBy),
          }))
        : groups;

    const tableData: TableData = {
      columns: COLUMNS,
      rows: sortedGroups.map(group => ({
        id: group.id,
        color: group.color,
        cells: COLUMNS.map(column => ({
          text: formatCellValue(group[column.key] ?? null, column.type),
          align: column.align,
        })),
        childRows: group.rows.map(row => ({
          cells: COLUMNS.map(column => ({
            text: formatCellValue(row[column.key] ?? null, column.type),
            align: column.align,
          })),
        })),
      })),
    };

    const rows = filterRows(tableData.rows, filter);

    if (!stateTabList && rows.length > 0) {
      dispatch(setGroupSelectionTabsList(tabList, TabSlug.Income));
    }

    if (!stateDefaultTabIndex) {
      dispatch(setGroupSelectionDefaultTabIndex(defaultTabIndex, TabSlug.Income));
    }

    return {
      tabList,
      tableData: {
        ...tableData,
        rows,
      },
      tabIndex,
    };
  }, [
    dispatch,
    filter,
    queryResult.data,
    sortBy,
    stateDefaultTabIndex,
    stateSelectedTabIndex,
    stateTabList,
  ]);

  return {
    ...queryResult,
    data,
  };
};
